"use client"

import { useState } from 'react'
import { MessageCircleQuestion, Plus } from 'lucide-react'
import { toast } from 'sonner'
import { getPageHeader, getFeedbackRequesterLabel } from '../../../constants'
import {
  useCreateFeedbackPrompt,
  useFeedbackPrompts,
  useUpdateFeedbackPrompt,
  type FeedbackKind,
  type FeedbackPrompt,
  type FeedbackPromptScope,
} from '../../../hooks/use-feedback'
import { useAdminBasePath, adminPath } from '@/components/admin/use-admin-base-path'
import { Badge } from '../../ui/badge'
import { Button } from '../../ui/button'
import { Card, CardContent } from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { Switch } from '../../ui/switch'
import { PageContainer } from '../../ui/page-container'
import { PageHeader } from '../../ui/page-header'
import { PageHeaderLeading } from '@/components/navigation/page-header-leading'
import { DashboardSectionSkeleton } from '@/components/dashboard/dashboard-section-skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'
import {
  FEEDBACK_KINDS,
  FEEDBACK_PROMPT_SCOPES,
  feedbackKindBadgeVariant,
  formatFeedbackKind,
  formatFeedbackPromptScope,
} from '@/lib/feedback-display'

const NO_KIND_HINT = 'none'

function PromptRow({
  prompt,
  onToggleActive,
  isUpdating,
}: {
  prompt: FeedbackPrompt
  onToggleActive: (prompt: FeedbackPrompt, active: boolean) => void
  isUpdating: boolean
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-4">
      <div className="min-w-0 space-y-1.5">
        <p className="break-words text-sm font-medium">{prompt.question}</p>
        <div className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
          <Badge variant="outline">{formatFeedbackPromptScope(prompt.scope)}</Badge>
          {prompt.kind_hint ? (
            <Badge variant={feedbackKindBadgeVariant(prompt.kind_hint)}>
              {formatFeedbackKind(prompt.kind_hint)}
            </Badge>
          ) : null}
          <span>
            {prompt.response_count ?? 0}{' '}
            {(prompt.response_count ?? 0) === 1 ? 'response' : 'responses'}
          </span>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Label htmlFor={`prompt-active-${prompt.id}`} className="text-xs text-muted-foreground">
          {prompt.active ? 'Active' : 'Inactive'}
        </Label>
        <Switch
          id={`prompt-active-${prompt.id}`}
          checked={prompt.active}
          disabled={isUpdating}
          onCheckedChange={(checked) => onToggleActive(prompt, checked)}
        />
      </div>
    </div>
  )
}

export function AdminFeedbackPromptsPage() {
  const basePath = useAdminBasePath()
  const [question, setQuestion] = useState('')
  const [kindHint, setKindHint] = useState<string>(NO_KIND_HINT)
  const [scope, setScope] = useState<FeedbackPromptScope>('board')

  const { prompts, isLoading, error } = useFeedbackPrompts()
  const { createPrompt, isCreating } = useCreateFeedbackPrompt()
  const { updatePrompt, isUpdating } = useUpdateFeedbackPrompt()

  const handleCreate = () => {
    const trimmed = question.trim()
    if (!trimmed) {
      toast.error('Enter a question first')
      return
    }
    createPrompt(
      {
        question: trimmed,
        scope,
        ...(kindHint !== NO_KIND_HINT ? { kind_hint: kindHint as FeedbackKind } : {}),
      },
      {
        onSuccess: () => {
          toast.success(
            scope === 'onboarding'
              ? 'Onboarding prompt created — private answers only'
              : 'Prompt created — the agent can now ask it',
          )
          setQuestion('')
          setKindHint(NO_KIND_HINT)
          setScope('board')
        },
        onError: (err: Error) => toast.error(err.message || 'Failed to create prompt'),
      },
    )
  }

  const handleToggleActive = (prompt: FeedbackPrompt, active: boolean) => {
    updatePrompt(
      { promptId: prompt.id, data: { active } },
      {
        onSuccess: () => toast.success(active ? 'Prompt activated' : 'Prompt deactivated'),
        onError: (err: Error) => toast.error(err.message || 'Failed to update prompt'),
      },
    )
  }

  if (error) {
    return (
      <PageContainer>
        <div className="text-center py-12">
          <p className="text-destructive">Error loading prompts: {(error as Error).message}</p>
        </div>
      </PageContainer>
    )
  }

  const requesterLabel = getFeedbackRequesterLabel()
  const { title, description } = getPageHeader(
    adminPath(basePath, 'feedback/prompts'),
    'Feedback prompts',
    `Questions the ${requesterLabel} asks — board prompts for testers, or private onboarding prompts.`,
  )

  return (
    <PageContainer>
        <PageHeader
            leading={<PageHeaderLeading />}
            title={title}
            description={description}
            icon={<MessageCircleQuestion className="h-5 w-5" />}
        />

        <Card>
          <CardContent className="space-y-3 pt-6">
            <div className="space-y-1.5">
              <Label htmlFor="new-prompt-question">New prompt</Label>
              <Input
                id="new-prompt-question"
                value={question}
                placeholder='e.g. "How are you finding the app so far?"'
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate()
                }}
              />
            </div>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="flex flex-wrap gap-3">
                <div className="space-y-1.5">
                  <Label>Scope</Label>
                  <Select
                    value={scope}
                    onValueChange={(value) => setScope(value as FeedbackPromptScope)}
                  >
                    <SelectTrigger className="w-[200px]" aria-label="Prompt scope">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FEEDBACK_PROMPT_SCOPES.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Expected kind (optional)</Label>
                  <Select value={kindHint} onValueChange={setKindHint}>
                    <SelectTrigger className="w-[200px]" aria-label="Expected feedback kind">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_KIND_HINT}>No hint</SelectItem>
                      {FEEDBACK_KINDS.map(({ value, label }) => (
                        <SelectItem key={value} value={value}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button type="button" onClick={handleCreate} disabled={isCreating}>
                <Plus className="size-4 mr-2" aria-hidden />
                {isCreating ? 'Creating...' : 'Create prompt'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <DashboardSectionSkeleton cards={3} />
        ) : prompts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageCircleQuestion className="size-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No prompts yet</h3>
              <p className="text-muted-foreground">
                Create a board prompt for chat elicitation, or an onboarding prompt for the wizard
                (platform-private answers).
              </p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="divide-y divide-border/60 py-2">
              {prompts.map((prompt) => (
                <PromptRow
                  key={prompt.id}
                  prompt={prompt}
                  onToggleActive={handleToggleActive}
                  isUpdating={isUpdating}
                />
              ))}
            </CardContent>
          </Card>
        )}
    </PageContainer>
  )
}
